import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, FileText } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const money = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v || 0);
  // Rule 10: a negative amount reads "−₹780", never "₹-780"
  return `${n < 0 ? "−" : ""}₹${Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};
const GRADE = {
  Healthy: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Watch: "bg-amber-100 text-amber-800 border-amber-300",
  Critical: "bg-orange-100 text-orange-800 border-orange-300",
  Dead: "bg-red-100 text-red-800 border-red-300",
};

function SsCard({ label, value, sub, tone = "text-zinc-950", testId }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testId}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">{label}</p>
      <p className={`font-mono text-2xl font-bold ${tone}`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-400 mt-1">{sub}</p>}
    </div>
  );
}

function Ledger({ title, note, rows, closing, testId, orderLinks, hidden }) {
  const navigate = useNavigate();
  if (hidden) return <p className="text-xs text-zinc-400 py-6" data-testid={`${testId}-hidden`}>You do not have permission to see payments — qcom_ss.view_payments required.</p>;
  return (
    <div data-testid={testId}>
      <h3 className="text-xs font-semibold text-zinc-600 mb-1">{title}</h3>
      {note && <p className="text-[10px] text-zinc-400 mb-2">{note}</p>}
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="data-table w-full">
          <thead><tr>
            <th>Date</th><th>Particulars</th><th className="text-right">Debit ₹</th>
            <th className="text-right">Credit ₹</th><th className="text-right">Balance ₹</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center text-xs text-zinc-400 py-5">Nothing on this ledger yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} data-testid={`${testId}-row-${i}`}>
                <td className="text-[11px]">{fmtDate(r.date)}</td>
                <td className="text-xs">
                  {orderLinks && r.order_id
                    ? <button className="text-blue-700 hover:underline" onClick={() => navigate(`/orders/${r.order_id}`)}>{r.particulars}</button>
                    : r.particulars}
                </td>
                <td className="text-right font-mono text-[11px]">{r.debit ? money(r.debit) : "—"}</td>
                <td className="text-right font-mono text-[11px]">{r.credit ? money(r.credit) : "—"}</td>
                <td className="text-right font-mono text-[11px] font-semibold">{money(r.balance)}</td>
              </tr>
            ))}
            <tr className="bg-zinc-50">
              <td colSpan={4} className="text-right text-[11px] font-semibold">Closing balance</td>
              <td className="text-right font-mono text-xs font-bold" data-testid={`${testId}-closing`}>{money(closing)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TABS = [["blinkit", "Ledger with Blinkit"], ["skippi", "Ledger with SKIPPI"],
  ["warehouses", "Warehouses"], ["trend", "Monthly trend"]];

export default function QcomSSDetailPage() {
  const { ssId } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(sp.get("sstab") || "blinkit");
  const [whSort, setWhSort] = useState({ by: "ordered", dir: "desc" });
  const [trSort, setTrSort] = useState({ by: "month", dir: "asc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/qcom-ss/ss/${ssId}`, {
        params: { date_from: sp.get("date_from") || "", date_to: sp.get("date_to") || "" } });
      setD(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open this Super Stockist");
    } finally { setLoading(false); }
  }, [ssId, sp]);

  useEffect(() => { load(); }, [load]);

  const sorted = (rows, s) => [...(rows || [])].sort((a, b) => {
    const x = a[s.by], y = b[s.by];
    const cmp = (x === null ? -1 : y === null ? 1 : x > y ? 1 : x < y ? -1 : 0);
    return s.dir === "asc" ? cmp : -cmp;
  });
  const warehouses = useMemo(() => sorted(d?.warehouses, whSort), [d, whSort]);
  const trend = useMemo(() => sorted(d?.monthly_trend, trSort), [d, trSort]);

  if (loading) return <div className="p-6 text-xs text-zinc-500">Loading…</div>;
  if (!d) return <div className="p-6 text-xs text-zinc-500">Not found.</div>;
  const sc = d.scorecard || {};
  const can = d.can || {};
  const Th = ({ col, children, right, state, set }) => (
    <th className={`cursor-pointer select-none ${right ? "text-right" : ""}`}
      onClick={() => set(s => (s.by === col ? { by: col, dir: s.dir === "asc" ? "desc" : "asc" } : { by: col, dir: "desc" }))}
      data-testid={`ss-sort-${col}`}>
      {children}{state.by === col ? (state.dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );

  return (
    <div className="p-6" data-testid="qcom-ss-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => navigate(-1)} className="text-[11px] text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-1" data-testid="qcom-ss-back">
            <ArrowLeft size={12} /> Qcom SS POs
          </button>
          <h1 className="text-2xl font-bold text-zinc-950" data-testid="qcom-ss-name">{d.ss_name}</h1>
          <p className="text-[11px] text-zinc-500">POs dated {fmtDate(d.date_from)} to {fmtDate(d.date_to)}</p>
        </div>
        <div className="flex items-center gap-2">
          {sc.grade && <span className={`px-2 py-1 text-[10px] font-semibold rounded-sm border ${GRADE[sc.grade]}`} data-testid="qcom-ss-grade">{sc.grade}</span>}
          {can.export && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/qcom-ss/export/ss/${ssId}`} data-testid="qcom-ss-pdf">
              <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs"><FileText size={12} className="mr-1" /> Report card (PDF)</Button>
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <SsCard label="POs" value={sc.pos || 0} sub={`${money(sc.ordered)} ordered`} testId="qcom-ss-card-pos" />
        <SsCard label="Open POs" value={sc.open_pos || 0} sub={`${sc.expiring_3 || 0} expire in 3 days`} tone="text-amber-700" testId="qcom-ss-card-open" />
        <SsCard label="Fill % (closed POs)" value={`${sc.fill_pct || 0}%`} sub={`target ${d.targets.fill_target}%`} tone="text-emerald-700" testId="qcom-ss-card-fill" />
        <SsCard label="Appt within 2 days" value={`${sc.appointment_within_2_pct || 0}%`} sub={`target ${d.targets.appointment_target}%`}
          tone={(sc.appointment_within_2_pct || 0) < d.targets.appointment_target ? "text-red-700" : "text-emerald-700"} testId="qcom-ss-card-appt" />
        <SsCard label="Platform owes SS" value={money(d.platform_owes_ss)} testId="qcom-ss-card-platform-owes" />
        <SsCard label="SS owes SKIPPI" value={money(d.ss_owes_skippi)} sub={d.ss_overdue ? `${money(d.ss_overdue)} overdue` : "nothing overdue"} tone="text-amber-700" testId="qcom-ss-card-ss-owes" />
        <SsCard label="Lost on expiry" value={money(sc.lost)} sub={`${sc.appointments_missed || 0} appointment(s) missed`} tone="text-red-700" testId="qcom-ss-card-lost" />
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="qcom-ss-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); const n = new URLSearchParams(sp); n.set("sstab", k); setSp(n); }}
            className={`px-4 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === k
              ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}
            data-testid={`qcom-ss-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "blinkit" && (
        <Ledger title="Ledger with Blinkit" note={d.platform_ledger.opening_note} hidden={d.platform_ledger.hidden}
          rows={d.platform_ledger.rows} closing={d.platform_ledger.closing_balance} testId="qcom-ledger-platform" />
      )}
      {tab === "skippi" && (
        <Ledger title="Ledger with SKIPPI" rows={d.skippi_ledger.rows} hidden={d.skippi_ledger.hidden}
          closing={d.skippi_ledger.closing_balance} testId="qcom-ledger-skippi" orderLinks />
      )}

      {tab === "warehouses" && (
        <div data-testid="qcom-ss-warehouses">
          <h3 className="text-xs font-semibold text-zinc-600 mb-2">Warehouses</h3>
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr>
                <Th col="facility_name" state={whSort} set={setWhSort}>Facility</Th>
                <Th col="city" state={whSort} set={setWhSort}>City</Th>
                <Th col="pos" right state={whSort} set={setWhSort}>POs</Th>
                <Th col="ordered" right state={whSort} set={setWhSort}>Ordered ₹</Th>
                <Th col="grn" right state={whSort} set={setWhSort}>GRN ₹</Th>
                <Th col="open_value" right state={whSort} set={setWhSort}>Open ₹</Th>
                <Th col="fill_pct" right state={whSort} set={setWhSort}>Fill %</Th>
              </tr></thead>
              <tbody>
                {warehouses.length === 0 && <tr><td colSpan={7} className="text-center text-xs text-zinc-400 py-5">No facility POs in this window.</td></tr>}
                {warehouses.map((w, i) => (
                  <tr key={i} data-testid={`qcom-warehouse-${i}`}>
                    <td className="text-xs font-medium">{w.facility_name}</td>
                    <td className="text-xs">{w.city}</td>
                    <td className="text-right text-[11px]">{w.pos}</td>
                    <td className="text-right font-mono text-[11px]">{money(w.ordered)}</td>
                    <td className="text-right font-mono text-[11px]">{money(w.grn)}</td>
                    <td className="text-right font-mono text-[11px]">{money(w.open_value)}</td>
                    <td className="text-right font-mono text-[11px]">{w.fill_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "trend" && (
        <div data-testid="qcom-ss-trend">
          <h3 className="text-xs font-semibold text-zinc-600 mb-2">Monthly trend — fill % on closed POs only</h3>
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="data-table w-full">
              <thead><tr>
                <Th col="month" state={trSort} set={setTrSort}>Month</Th>
                <Th col="pos" right state={trSort} set={setTrSort}>POs</Th>
                <Th col="ordered" right state={trSort} set={setTrSort}>Ordered ₹</Th>
                <Th col="closed_ordered" right state={trSort} set={setTrSort}>Closed ₹</Th>
                <Th col="grn" right state={trSort} set={setTrSort}>GRN ₹</Th>
                <Th col="fill_pct" right state={trSort} set={setTrSort}>Fill %</Th>
                <th></th>
              </tr></thead>
              <tbody>
                {trend.length === 0 && <tr><td colSpan={7} className="text-center text-xs text-zinc-400 py-5">No months yet.</td></tr>}
                {trend.map((m, i) => (
                  <tr key={i} data-testid={`qcom-trend-${i}`}>
                    <td className="text-xs font-medium">{m.month}</td>
                    <td className="text-right text-[11px]">{m.pos}</td>
                    <td className="text-right font-mono text-[11px]">{money(m.ordered)}</td>
                    <td className="text-right font-mono text-[11px]">{money(m.closed_ordered)}</td>
                    <td className="text-right font-mono text-[11px]">{money(m.grn)}</td>
                    <td className="text-right font-mono text-[11px]">{m.fill_pct}%</td>
                    <td className="w-[180px]">
                      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${Math.min(m.fill_pct, 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
